// src/utilities/colorUtils.ts

/**
 * Checks if a string is a valid hex color (#xxx or #xxxxxx).
 */
export const isValidHexColor = (color: string): boolean => {
    if (!color || typeof color !== 'string') {
        return false;
    }
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexColorRegex.test(color);
};

/**
 * Converts a hex color string to an RGB array.
 */
const hexToRgb = (hex: string): [number, number, number] | null => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : null;
};

/**
 * Converts an RGB color array to an HSL array.
 */
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return [h * 360, s * 100, l * 100];
};

/**
 * Converts an HSL array to a hex color string.
 */
const hslToHex = (h: number, s: number, l: number): string => {
    s /= 100;
    l /= 100;
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h / 360 + 1 / 3);
        g = hue2rgb(p, q, h / 360);
        b = hue2rgb(p, q, h / 360 - 1 / 3);
    }

    const toHex = (c: number) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Takes a hex color and ensures it's dark enough to be readable as text
 * against a light background by capping its HSL lightness.
 * @param {string} hex The hex color string to adjust.
 * @returns {string} The adjusted, readable hex color string.
 */
const getReadableTextColor = (hex: string): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex; // Return original if conversion fails

    let [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);

    // **KEY CHANGE**: Cap the lightness at 45%.
    // This forces bright colors (like yellow) to become a darker shade,
    // ensuring they have sufficient contrast against a light background.
    const MAX_LIGHTNESS = 45;
    if (l > MAX_LIGHTNESS) {
        l = MAX_LIGHTNESS;
    }

    return hslToHex(h, s, l);
};


/**
 * Generates tag colors with a lighter, transparent background and readable text.
 * @param {string | null | undefined} hexColor The raw hex string from the Tag object.
 * @param {string} defaultTagColor The default color to use if the hex is invalid.
 * @returns {{tagColor: string, textColor: string}} The styled background and text colors.
 */
export const getStyledTagColors = (
    hexColor: string | null | undefined,
    defaultTagColor: string
): { tagColor: string; textColor: string } => {
    const baseColor = (hexColor && isValidHexColor(hexColor)) ? hexColor : defaultTagColor;
    
    // Generate a readable text color by adjusting the base color
    const textColor = getReadableTextColor(baseColor);

    // The background is always a transparent version of the original base color
    const rgb = hexToRgb(baseColor);

    if (!rgb) {
        return {
            tagColor: 'rgba(108, 117, 125, 0.15)',
            textColor: defaultTagColor,
        };
    }

    const OPACITY = 0.15;
    const tagColor = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${OPACITY})`;

    return {
        tagColor,
        textColor,
    };
};

export const getStatusColors = (statusName?: string) => {
    let backgroundColor: string;
    switch (statusName?.toLowerCase()) {
        case 'closed':
            backgroundColor = '#6c757d'; // Bootstrap secondary gray
            break;
        case 'in progress':
            backgroundColor = '#007bff'; // Bootstrap primary blue
            break;
        case 'ready for qa':
            backgroundColor = '#ffc107'; // Bootstrap warning yellow
            break;
        case 'in review':
            backgroundColor = '#ffc107'; // Bootstrap warning yellow
            break;
        case 'awaiting release':
            backgroundColor = '#7e57c2'; // Bootstrap purple (success-like)
            break;
        case 'done':
            backgroundColor = '#28a745'; // Bootstrap success green
            break;
        case 're-opened':
            backgroundColor = '#dc3545'; // Bootstrap danger red
            break;
        case 'open':
        default:
            backgroundColor = '#6c757d'; // Default to a neutral gray for 'Open' and others
            break;
    }

    const textColor = getReadableTextColor(backgroundColor);

    return {
        backgroundColor,
        textColor
    };
};